import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import {
  FaChevronDown,
  FaCheck,
  FaEdit,
  FaExternalLinkAlt,
  FaEye,
  FaEyeSlash,
  FaFolderPlus,
  FaKey,
  FaPlus,
  FaSave,
  FaSearch,
  FaSyncAlt,
  FaTimes,
  FaTrash,
} from "react-icons/fa";
import ConfirmationModal from "../components/common/ConfirmationModal";
import { AuthContext } from "../context/AuthContext";
import { useToast } from "../context/ToastContext";
import api from "../services/api";
import { extractErrorMessage } from "../utils/errorUtils";
import {
  createVaultCategory,
  createVaultEntry,
  deleteVaultEntry,
  listVaultCategories,
  listVaultCategoryPermissions,
  listVaultEntries,
  revealVaultPassword,
  updateVaultCategory,
  updateVaultCategoryPermission,
  updateVaultEntry,
  deleteVaultCategory,
} from "../services/passwordVaultService";
import { hasRole, isAdminUser } from "../utils/roleUtils";
import { can, FEATURE_KEYS } from "../utils/featureAccess";
import "./PasswordVault.css";

const EMPTY_FORM = {
  siteName: "",
  websiteUrl: "",
  username: "",
  password: "",
  categoryPath: "",
  notes: "",
};

const EMPTY_FOLDER_FORM = {
  name: "",
  addAsSubfolder: false,
  parentPath: "",
};

const CATEGORY_PATH_SEPARATOR = "\\";
const UNCATEGORIZED_CATEGORY = "Uncategorized";

const normalizeUrl = (url) => {
  const trimmed = url.trim();
  if (!trimmed) return "";
  if (/^https?:\/\//i.test(trimmed)) return trimmed;
  return `https://${trimmed}`;
};

const splitCategoryPath = (value) =>
  String(value || "")
    .split(/[\\/]+/)
    .map((segment) => segment.trim())
    .filter(Boolean);

const joinCategoryPath = (segments) => segments.join(CATEGORY_PATH_SEPARATOR);

const normalizeCategoryPath = (value, fallback = "") => {
  const segments = splitCategoryPath(value);
  if (segments.length > 0) return joinCategoryPath(segments);
  return fallback;
};

const getCategoryLabelFromPath = (path) => {
  const segments = splitCategoryPath(path);
  if (segments.length === 0) return path || UNCATEGORIZED_CATEGORY;
  return segments[segments.length - 1];
};

const getCategoryParentPath = (path) => {
  const segments = splitCategoryPath(path);
  if (segments.length <= 1) return "";
  return joinCategoryPath(segments.slice(0, -1));
};

const isSameOrDescendantCategoryPath = (value, parentPath) => {
  if (!parentPath || parentPath === "all") return true;
  if (value === parentPath) return true;
  return value.startsWith(`${parentPath}${CATEGORY_PATH_SEPARATOR}`);
};

const normalizeEntry = (entry) => ({
  id: String(entry?.id ?? entry?.entryId ?? ""),
  siteName: entry?.siteName ?? entry?.site_name ?? "",
  websiteUrl: entry?.websiteUrl ?? entry?.website_url ?? "",
  username: entry?.username ?? entry?.login_username ?? "",
  categoryId: String(entry?.categoryId ?? entry?.category_id ?? ""),
  categoryName: normalizeCategoryPath(
    entry?.categoryName ?? entry?.category_name ?? ""
  ),
  notes: entry?.notes ?? "",
});

const normalizeCategory = (category) => {
  const id = String(category?.id ?? category?.categoryId ?? "");
  const path = normalizeCategoryPath(category?.name ?? category?.categoryName ?? "");
  const parentPath = getCategoryParentPath(path);
  return {
    id,
    name: path,
    label: getCategoryLabelFromPath(path),
    parentPath,
    depth: Math.max(splitCategoryPath(path).length - 1, 0),
  };
};

const normalizeEmployee = (employee) => {
  const id = String(employee?.id ?? employee?.employeeId ?? "");
  return {
    id,
    name: employee?.name || employee?.email || `User ${id}`,
    email: employee?.email || "",
    role: employee?.role || "",
    roles: Array.isArray(employee?.roles) ? employee.roles : [],
  };
};

const normalizePermission = (permission) => ({
  employeeId: String(permission?.employeeId ?? permission?.userId ?? ""),
  enabled: Boolean(
    permission?.enabled ?? permission?.canAccess ?? permission?.allowed
  ),
});

const toPayload = (formValues, includePassword, categoryId) => {
  const payload = {
    siteName: formValues.siteName.trim(),
    websiteUrl: normalizeUrl(formValues.websiteUrl),
    username: formValues.username.trim(),
    categoryId,
    notes: formValues.notes.trim(),
  };
  if (includePassword) {
    payload.password = formValues.password;
  }
  return payload;
};

const CARD_TONES = ["tone-a", "tone-b", "tone-c", "tone-d", "tone-e"];

const pickCardTone = (value) => {
  const source = String(value || "");
  let hash = 0;
  for (let i = 0; i < source.length; i += 1) {
    hash = (hash * 31 + source.charCodeAt(i)) % 2147483647;
  }
  return CARD_TONES[Math.abs(hash) % CARD_TONES.length];
};

const resolveEntryCategoryPath = (entry, categoryLookup) =>
  normalizeCategoryPath(
    entry?.categoryName || categoryLookup[String(entry?.categoryId ?? "")] || "",
    UNCATEGORIZED_CATEGORY
  );

const toFolderDisplayPath = (path) => splitCategoryPath(path).join("/");

const formatCategoryOptionLabel = (path) => {
  const depth = Math.max(splitCategoryPath(path).length - 1, 0);
  const label = getCategoryLabelFromPath(path);
  return `${"  ".repeat(depth)}${label}`;
};

function PasswordVault() {
  const { user } = useContext(AuthContext);
  const { addToast } = useToast();
  // Content CRUD (entries + folders) is gated on the Password Vault feature's
  // write/delete capabilities. `isAdmin` is retained only for the per-category
  // ACL ("Folder Permissions" — deciding which employees can see a folder),
  // which is a meta-admin concern, not read/write/delete of vault content.
  const isAdmin = isAdminUser(user);
  const canWrite = can(user, FEATURE_KEYS.PASSWORD_VAULT, "write");
  const canDelete = can(user, FEATURE_KEYS.PASSWORD_VAULT, "delete");

  const [entries, setEntries] = useState([]);
  const [categories, setCategories] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState(EMPTY_FORM);
  const [editingId, setEditingId] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [categoryFilter, setCategoryFilter] = useState("all");
  const [revealedPasswords, setRevealedPasswords] = useState({});
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [permissionCategoryId, setPermissionCategoryId] = useState("");
  const [permissionsByEmployee, setPermissionsByEmployee] = useState({});
  const [permissionsLoading, setPermissionsLoading] = useState(false);
  const [permissionSavingIds, setPermissionSavingIds] = useState({});
  const [showEntryForm, setShowEntryForm] = useState(false);
  const [showPermissionsModal, setShowPermissionsModal] = useState(false);
  const [showFormPassword, setShowFormPassword] = useState(false);
  const [showQuickAddMenu, setShowQuickAddMenu] = useState(false);
  const [showFolderForm, setShowFolderForm] = useState(false);
  const [folderSaving, setFolderSaving] = useState(false);
  const [folderForm, setFolderForm] = useState(EMPTY_FOLDER_FORM);
  const [editingFolderId, setEditingFolderId] = useState(null);
  const [deleteFolderTarget, setDeleteFolderTarget] = useState(null);

  const isEditing = editingId !== null;
  const categoryLookup = useMemo(() => {
    const next = {};
    categories.forEach((category) => {
      next[category.id] = category.name;
    });
    return next;
  }, [categories]);

  const sortedCategories = useMemo(
    () =>
      [...categories].sort((a, b) =>
        a.name.localeCompare(b.name, undefined, { sensitivity: "base" })
      ),
    [categories]
  );

  const loadBaseData = useCallback(async () => {
    setLoading(true);
    try {
      const [entryResult, categoryResult, employeeResult] = await Promise.allSettled(
        [
          listVaultEntries(),
          listVaultCategories(),
          isAdmin
            ? api.get("/employees").then((response) => response.data)
            : Promise.resolve([]),
        ]
      );

      if (entryResult.status === "rejected") {
        addToast("Unable to load vault entries.", "error");
      }
      if (categoryResult.status === "rejected") {
        addToast("Unable to load vault folders.", "error");
      }
      if (isAdmin && employeeResult.status === "rejected") {
        addToast("Unable to load employees for folder permissions.", "error");
      }

      const entryRows =
        entryResult.status === "fulfilled" && Array.isArray(entryResult.value)
          ? entryResult.value
          : [];
      const categoryRows =
        categoryResult.status === "fulfilled" && Array.isArray(categoryResult.value)
          ? categoryResult.value
          : [];
      const employeeRows =
        employeeResult.status === "fulfilled" ? employeeResult.value : [];

      const normalizedCategories = categoryRows
        .map(normalizeCategory)
        .filter((category) => category.id && category.name);
      const normalizedEntries = entryRows
        .map(normalizeEntry)
        .filter((entry) => entry.id);
      const normalizedEmployees = Array.isArray(employeeRows)
        ? employeeRows
          .map(normalizeEmployee)
          .filter((employee) => employee.id && !hasRole(employee, "Admin") && !hasRole(employee, "Super Admin"))
        : [];

      setCategories(normalizedCategories);
      setEntries(normalizedEntries);
      setEmployees(normalizedEmployees);

      setForm((prev) => ({
        ...prev,
        categoryPath:
          prev.categoryPath ||
          toFolderDisplayPath(normalizedCategories[0]?.name || "") ||
          prev.categoryPath,
      }));
      setPermissionCategoryId((prev) => {
        if (!isAdmin) return "";
        const stillExists = normalizedCategories.some((category) => category.id === prev);
        if (stillExists) return prev;
        return normalizedCategories[0]?.id || "";
      });
    } catch (error) {
      const errorMessage = await extractErrorMessage(error, "Unable to load password vault data.");
      addToast(errorMessage, "error");
    } finally {
      setLoading(false);
    }
  }, [addToast, isAdmin]);

  useEffect(() => {
    loadBaseData();
  }, [loadBaseData]);

  useEffect(() => {
    if (categoryFilter === "all") return;
    const hasMatchingCategory = entries.some((entry) => {
      const path = resolveEntryCategoryPath(entry, categoryLookup);
      return isSameOrDescendantCategoryPath(path, categoryFilter);
    });
    const hasMatchingFolder = categories.some((category) =>
      isSameOrDescendantCategoryPath(category.name, categoryFilter)
    );
    if (!hasMatchingCategory && !hasMatchingFolder) {
      setCategoryFilter("all");
    }
  }, [categoryFilter, entries, categoryLookup, categories]);

  const loadCategoryPermissions = useCallback(
    async (categoryId) => {
      if (!isAdmin || !categoryId) {
        setPermissionsByEmployee({});
        return;
      }

      setPermissionsLoading(true);
      try {
        const permissionRows = await listVaultCategoryPermissions(categoryId);
        const nextPermissions = {};
        permissionRows.map(normalizePermission).forEach((permission) => {
          if (permission.employeeId) {
            nextPermissions[permission.employeeId] = permission.enabled;
          }
        });
        setPermissionsByEmployee(nextPermissions);
      } catch (error) {
        const errorMessage = await extractErrorMessage(error, "Unable to load folder permissions.");
        addToast(errorMessage, "error");
        setPermissionsByEmployee({});
      } finally {
        setPermissionsLoading(false);
      }
    },
    [addToast, isAdmin]
  );

  useEffect(() => {
    if (!isAdmin || !permissionCategoryId) return;
    loadCategoryPermissions(permissionCategoryId);
  }, [isAdmin, permissionCategoryId, loadCategoryPermissions]);

  const filteredEntries = useMemo(() => {
    const q = searchTerm.trim().toLowerCase();

    return entries.filter((entry) => {
      const entryCategoryPath = resolveEntryCategoryPath(entry, categoryLookup);
      const passesCategory =
        categoryFilter === "all" ||
        isSameOrDescendantCategoryPath(entryCategoryPath, categoryFilter);
      if (!passesCategory) return false;

      if (!q) return true;
      const haystack = [
        entry.siteName,
        entry.websiteUrl,
        entry.username,
        entryCategoryPath,
      ]
        .join(" ")
        .toLowerCase();
      return haystack.includes(q);
    });
  }, [categoryFilter, categoryLookup, entries, searchTerm]);

  const groupedCategoryNodes = useMemo(() => {
    const q = searchTerm.trim().toLowerCase();
    const entriesByPath = {};
    filteredEntries.forEach((entry) => {
      const path = resolveEntryCategoryPath(entry, categoryLookup);
      if (!entriesByPath[path]) entriesByPath[path] = [];
      entriesByPath[path].push(entry);
    });

    const explicitCategoryPaths = new Set();
    categories.forEach((category) => {
      const path = normalizeCategoryPath(category.name);
      if (!path) return;
      if (
        categoryFilter !== "all" &&
        !isSameOrDescendantCategoryPath(path, categoryFilter)
      ) {
        return;
      }
      if (q && !path.toLowerCase().includes(q) && !entriesByPath[path]) {
        return;
      }
      explicitCategoryPaths.add(path);
    });

    const nodesByPath = {};
    const ensureNode = (path) => {
      if (!nodesByPath[path]) {
        const parentPath = getCategoryParentPath(path);
        nodesByPath[path] = {
          path,
          label: getCategoryLabelFromPath(path),
          depth: Math.max(splitCategoryPath(path).length - 1, 0),
          parentPath,
          entries: [],
          children: [],
          totalCount: 0,
          id: categories.find(c => normalizeCategoryPath(c.name) === path)?.id || null,
        };
      }
      return nodesByPath[path];
    };

    [...explicitCategoryPaths, ...Object.keys(entriesByPath)].forEach((path) => {
      if (!path) return;
      const segments = splitCategoryPath(path);
      for (let i = 1; i <= segments.length; i += 1) {
        ensureNode(joinCategoryPath(segments.slice(0, i)));
      }
      if (entriesByPath[path]) {
        ensureNode(path).entries = entriesByPath[path];
      }
    });

    Object.values(nodesByPath).forEach((node) => {
      if (!node.parentPath) return;
      const parentNode = nodesByPath[node.parentPath];
      if (parentNode) {
        parentNode.children.push(node);
      }
    });

    const sortNodes = (list) =>
      list.sort((a, b) =>
        a.path.localeCompare(b.path, undefined, { sensitivity: "base" })
      );

    const computeCounts = (node) => {
      sortNodes(node.children);
      let total = node.entries.length;
      node.children.forEach((childNode) => {
        total += computeCounts(childNode);
      });
      node.totalCount = total;
      return total;
    };

    const hasExplicitPathInTree = (node) => {
      if (explicitCategoryPaths.has(node.path)) return true;
      return node.children.some((childNode) => hasExplicitPathInTree(childNode));
    };

    const roots = sortNodes(
      Object.values(nodesByPath).filter(
        (node) => !node.parentPath || !nodesByPath[node.parentPath]
      )
    );

    roots.forEach((node) => computeCounts(node));
    return roots.filter(
      (node) => node.totalCount > 0 || hasExplicitPathInTree(node)
    );
  }, [categories, categoryFilter, categoryLookup, filteredEntries, searchTerm]);

  const resetForm = () => {
    setForm((prev) => ({
      ...EMPTY_FORM,
      categoryPath: toFolderDisplayPath(categories[0]?.name || "") || prev.categoryPath || "",
    }));
    setEditingId(null);
  };

  const handleChange = (field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const resolveCategoryIdFromPath = async (rawPath) => {
    const normalizedPath = normalizeCategoryPath(rawPath);
    if (!normalizedPath) {
      throw new Error("Folder is required.");
    }

    const existing = categories.find(
      (category) => category.name.toLowerCase() === normalizedPath.toLowerCase()
    );
    if (existing) {
      return existing.id;
    }

    if (!canWrite) {
      throw new Error("Folder does not exist. Ask someone with vault access to create it.");
    }

    const created = await createVaultCategory({ name: normalizedPath });
    const normalizedCreated = normalizeCategory(
      created?.category ? created.category : created
    );

    if (normalizedCreated.id && normalizedCreated.name) {
      setCategories((prev) => {
        const alreadyExists = prev.some((category) => category.id === normalizedCreated.id);
        if (alreadyExists) return prev;
        return [...prev, normalizedCreated];
      });
      return normalizedCreated.id;
    }

    const refreshedCategories = (await listVaultCategories())
      .map(normalizeCategory)
      .filter((category) => category.id && category.name);
    setCategories(refreshedCategories);

    const refreshedMatch = refreshedCategories.find(
      (category) => category.name.toLowerCase() === normalizedPath.toLowerCase()
    );
    if (refreshedMatch) return refreshedMatch.id;
    throw new Error("Folder could not be resolved.");
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (
      !form.siteName.trim() ||
      !form.websiteUrl.trim() ||
      !form.username.trim() ||
      !form.categoryPath.trim()
    ) {
      addToast("Site, URL, username, and folder are required.", "error");
      return;
    }

    if (!isEditing && !form.password) {
      addToast("Password is required when creating a vault entry.", "error");
      return;
    }

    setSaving(true);
    try {
      const categoryId = await resolveCategoryIdFromPath(form.categoryPath);
      const includePassword = !isEditing || Boolean(form.password);
      const payload = toPayload(form, includePassword, categoryId);

      if (isEditing) {
        await updateVaultEntry(editingId, payload);
        addToast("Vault entry updated.", "success");
      } else {
        await createVaultEntry(payload);
        addToast("Vault entry saved.", "success");
      }

      resetForm();
      setShowEntryForm(false);
      setShowFormPassword(false);
      setRevealedPasswords({});
      await loadBaseData();
    } catch (error) {
      const errorMessage = await extractErrorMessage(error, "Unable to save vault entry.");
      addToast(errorMessage, "error");
    } finally {
      setSaving(false);
    }
  };

  const handleEdit = (entry) => {
    setEditingId(entry.id);
    setForm({
      siteName: entry.siteName,
      websiteUrl: entry.websiteUrl,
      username: entry.username,
      password: "",
      categoryPath: toFolderDisplayPath(
        resolveEntryCategoryPath(entry, categoryLookup) || categories[0]?.name || ""
      ),
      notes: entry.notes || "",
    });
    setShowEntryForm(true);
    setShowFormPassword(false);
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    try {
      await deleteVaultEntry(deleteTarget.id);
      setEntries((prev) => prev.filter((entry) => entry.id !== deleteTarget.id));
      setRevealedPasswords((prev) => {
        const next = { ...prev };
        delete next[deleteTarget.id];
        return next;
      });
      addToast("Vault entry deleted.", "success");
    } catch (error) {
      const errorMessage = await extractErrorMessage(error, "Unable to delete vault entry.");
      addToast(errorMessage, "error");
    }
  };

  const copyToClipboard = async (value, successMessage) => {
    if (!value) {
      addToast("No value available to copy.", "error");
      return;
    }

    if (!navigator?.clipboard?.writeText) {
      addToast("Clipboard access is not available in this browser.", "error");
      return;
    }

    try {
      await navigator.clipboard.writeText(value);
      addToast(successMessage, "success");
    } catch (error) {
      addToast("Clipboard access failed.", "error");
    }
  };

  const fetchPassword = async (entryId) => {
    const existing = revealedPasswords[entryId];
    if (existing) return existing;

    const password = await revealVaultPassword(entryId);
    if (!password) {
      throw new Error("Password not returned from API.");
    }

    setRevealedPasswords((prev) => ({ ...prev, [entryId]: password }));
    window.setTimeout(() => {
      setRevealedPasswords((prev) => {
        if (!prev[entryId]) return prev;
        const next = { ...prev };
        delete next[entryId];
        return next;
      });
    }, 20000);

    return password;
  };

  const handleLaunch = async (entry) => {
    const launchUrl = normalizeUrl(entry.websiteUrl);
    const tab = window.open(launchUrl, "_blank", "noopener,noreferrer");

    if (!tab) {
      addToast("Browser blocked opening the website.", "error");
      return;
    }

    try {
      const password = await fetchPassword(entry.id);
      await copyToClipboard(
        password,
        "Website opened. Password copied. Paste it on the login page."
      );
    } catch (error) {
      addToast("Website opened. Password could not be copied.", "info");
    }
  };

  const handlePermissionToggle = async (employeeId, enabled) => {
    if (!permissionCategoryId) return;
    setPermissionSavingIds((prev) => ({ ...prev, [employeeId]: true }));

    try {
      await updateVaultCategoryPermission(permissionCategoryId, employeeId, enabled);
      setPermissionsByEmployee((prev) => ({ ...prev, [employeeId]: enabled }));
      addToast("Permission updated.", "success");
    } catch (error) {
      const errorMessage = await extractErrorMessage(error, "Unable to update permission.");
      addToast(errorMessage, "error");
    } finally {
      setPermissionSavingIds((prev) => {
        const next = { ...prev };
        delete next[employeeId];
        return next;
      });
    }
  };

  const openCreateForm = () => {
    setShowQuickAddMenu(false);
    setEditingId(null);
    setForm({
      ...EMPTY_FORM,
      categoryPath: toFolderDisplayPath(categories[0]?.name || ""),
    });
    setShowFormPassword(false);
    setShowEntryForm(true);
  };

  const closeEntryForm = () => {
    resetForm();
    setShowFormPassword(false);
    setShowEntryForm(false);
  };

  const openCreateFolderForm = () => {
    setShowQuickAddMenu(false);
    const defaultParentPath =
      categoryFilter !== "all" ? categoryFilter : categories[0]?.name || "";
    setFolderForm({
      ...EMPTY_FOLDER_FORM,
      addAsSubfolder: Boolean(defaultParentPath),
      parentPath: defaultParentPath,
    });
    setShowFolderForm(true);
  };

  const closeFolderForm = () => {
    setShowFolderForm(false);
    setFolderSaving(false);
    setFolderForm(EMPTY_FOLDER_FORM);
    setEditingFolderId(null);
  };

  const handleEditFolder = (node) => {
    if (!node.id) return;
    const segments = splitCategoryPath(node.path);
    const name = segments[segments.length - 1];
    const parentPath = getCategoryParentPath(node.path);

    setEditingFolderId(node.id);
    setFolderForm({
      name,
      addAsSubfolder: Boolean(parentPath),
      parentPath,
    });
    setShowFolderForm(true);
  };

  const handleConfirmDeleteFolder = (node) => {
    if (!node.id) return;
    setDeleteFolderTarget(node);
  };

  const handleDeleteFolder = async () => {
    if (!deleteFolderTarget) return;
    try {
      await deleteVaultCategory(deleteFolderTarget.id);
      addToast("Folder deleted.", "success");
      setDeleteFolderTarget(null);
      await loadBaseData();
    } catch (error) {
      const errorMessage = await extractErrorMessage(error, "Unable to delete folder.");
      addToast(errorMessage, "error");
    }
  };

  const handleFolderFormChange = (field, value) => {
    setFolderForm((prev) => ({ ...prev, [field]: value }));
  };

  const handleSaveFolder = async (event) => {
    event.preventDefault();

    if (!canWrite) {
      addToast("You don't have permission to manage folders.", "error");
      return;
    }

    const folderName = folderForm.name.trim();
    if (!folderName) {
      addToast("Folder name is required.", "error");
      return;
    }

    if (/[\\/]/.test(folderName)) {
      addToast("Enter only the folder name. Use Parent Folder for nesting.", "error");
      return;
    }

    if (folderForm.addAsSubfolder && !folderForm.parentPath) {
      addToast("Select a parent folder.", "error");
      return;
    }

    const fullPath = normalizeCategoryPath(
      folderForm.addAsSubfolder
        ? `${folderForm.parentPath}${CATEGORY_PATH_SEPARATOR}${folderName}`
        : folderName
    );

    if (
      !editingFolderId &&
      categories.some((category) => category.name.toLowerCase() === fullPath.toLowerCase())
    ) {
      addToast("Folder already exists.", "error");
      return;
    }

    setFolderSaving(true);
    try {
      if (editingFolderId) {
        await updateVaultCategory(editingFolderId, { name: fullPath });
        addToast("Folder updated.", "success");
      } else {
        const created = await createVaultCategory({ name: fullPath });
        const normalizedCreated = normalizeCategory(
          created?.category ? created.category : created
        );

        if (normalizedCreated.id && normalizedCreated.name) {
          setCategories((prev) => [...prev, normalizedCreated]);
        } else {
          const refreshedCategories = (await listVaultCategories())
            .map(normalizeCategory)
            .filter((category) => category.id && category.name);
          setCategories(refreshedCategories);
        }
        addToast("Folder created.", "success");
      }

      setCategoryFilter(fullPath);
      closeFolderForm();
      await loadBaseData();
    } catch (error) {
      addToast(error?.message || "Unable to save folder.", "error");
    } finally {
      setFolderSaving(false);
    }
  };

  const renderEntryCard = (entry) => {
    return (
      <article
        className={`vault-entry-card ${pickCardTone(`${entry.categoryId}-${entry.id}`)}`}
        key={entry.id}
      >
        <div className="vault-entry-avatar">
          <FaKey />
        </div>

        <div className="vault-entry-main">
          <h3 className="vault-entry-title">{entry.siteName}</h3>
        </div>

        <div className="vault-entry-actions">
          <button
            type="button"
            className="vault-btn secondary compact"
            onClick={() => handleLaunch(entry)}
          >
            <FaExternalLinkAlt /> Launch
          </button>
          {(canWrite || canDelete) && (
            <div className="vault-entry-hover-actions">
              {canWrite && (
                <button
                  type="button"
                  className="vault-icon-btn"
                  onClick={() => handleEdit(entry)}
                  title="Edit entry"
                  aria-label={`Edit ${entry.siteName}`}
                >
                  <FaEdit />
                </button>
              )}
              {canDelete && (
                <button
                  type="button"
                  className="vault-icon-btn danger"
                  onClick={() => setDeleteTarget(entry)}
                  title="Delete entry"
                  aria-label={`Delete ${entry.siteName}`}
                >
                  <FaTrash />
                </button>
              )}
            </div>
          )}
        </div>
      </article>
    );
  };

  const renderVaultEmptyState = () => {
    if (entries.length === 0 && categories.length === 0) {
      return "No vault entries yet. Add your first one.";
    }
    if (searchTerm.trim() || categoryFilter !== "all") {
      return "No entries or folders match your search and folder filter.";
    }
    return "No entries in this folder yet.";
  };

  const hasVisibleGroups = groupedCategoryNodes.length > 0;

  const renderCategoryGroup = (node) => (
    <div
      className="vault-group"
      key={node.path}
      style={{ "--vault-category-depth": node.depth }}
    >
      <div className="vault-group-heading-row">
        <div className={`vault-group-heading ${categoryFilter === node.path ? "active" : ""}`}>
          <button
            type="button"
            className="vault-group-label"
            onClick={() => setCategoryFilter((prev) => (prev === node.path ? "all" : node.path))}
            title="Filter by this folder"
          >
            {node.label}
            <span>{node.totalCount}</span>
          </button>
          {(canWrite || canDelete) && node.id && (
            <div className="vault-group-actions">
              {canWrite && (
                <button
                  type="button"
                  className="vault-icon-btn plain compact"
                  onClick={() => handleEditFolder(node)}
                  title="Edit folder"
                >
                  <FaEdit />
                </button>
              )}
              {canDelete && (
                <button
                  type="button"
                  className="vault-icon-btn plain compact danger"
                  onClick={() => handleConfirmDeleteFolder(node)}
                  title="Delete folder"
                >
                  <FaTrash />
                </button>
              )}
            </div>
          )}
        </div>
      </div>

      {node.entries.length > 0 && (
        <div className="vault-grid vault-group-grid">
          {node.entries.map((entry) => renderEntryCard(entry))}
        </div>
      )}

      {node.children.length > 0 && (
        <div className="vault-group-children">
          {node.children.map((childNode) => renderCategoryGroup(childNode))}
        </div>
      )}
    </div>
  );

  return (
    <div className="vault-page">
      <header className="vault-header">
        <div className="vault-header-titles">
          <h1>Password Vault</h1>
        </div>
        <div className="vault-top-actions">
          <div className="vault-search compact">
            <FaSearch className="vault-search-icon" />
            <input
              type="text"
              placeholder="Search site, URL, username..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
          <button
            type="button"
            className="vault-btn ghost compact"
            onClick={loadBaseData}
            disabled={loading}
            title="Refresh entries"
          >
            <FaSyncAlt />
          </button>
          {isAdmin && (
            <button
              type="button"
              className="vault-btn secondary compact"
              onClick={() => setShowPermissionsModal(true)}
            >
              Folder Permissions
            </button>
          )}
          {canWrite && (
            <div
              className="vault-quick-add"
              onMouseEnter={() => setShowQuickAddMenu(true)}
              onMouseLeave={() => setShowQuickAddMenu(false)}
            >
              <button
                type="button"
                className="vault-btn primary compact"
                onClick={() => setShowQuickAddMenu((prev) => !prev)}
                aria-expanded={showQuickAddMenu}
                aria-haspopup="menu"
              >
                <FaPlus /> Add <FaChevronDown />
              </button>
              {showQuickAddMenu && (
                <div className="vault-quick-add-menu" role="menu">
                  <div className="vault-quick-add-content">
                    <button type="button" role="menuitem" onClick={openCreateForm}>
                      <FaPlus /> Add Password
                    </button>
                    <button
                      type="button"
                      role="menuitem"
                      onClick={openCreateFolderForm}
                    >
                      <FaFolderPlus /> Add Folder
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </header>

      <section className="vault-panel vault-list-panel">
        <div className="vault-panel-toolbar">
          <h2 className="vault-panel-title">
            <FaKey /> Saved Logins
          </h2>
          <div className="vault-category-strip">
            <button
              type="button"
              className={`vault-chip ${categoryFilter === "all" ? "active" : ""}`}
              onClick={() => setCategoryFilter("all")}
            >
              All Items
              <span>{entries.length}</span>
            </button>
            {categoryFilter !== "all" && (
              <button
                type="button"
                className="vault-chip active"
                onClick={() => setCategoryFilter("all")}
                title="Clear folder filter"
              >
                {categoryFilter}
                <span>{filteredEntries.length}</span>
              </button>
            )}
          </div>
          <div className="vault-list-meta">
            {categoryFilter !== "all" && (
              <button
                type="button"
                className="vault-btn ghost compact"
                onClick={() => setCategoryFilter("all")}
              >
                Clear Folder Filter
              </button>
            )}
            <p>{filteredEntries.length} visible</p>
          </div>
        </div>

        <div className="vault-list-body">
          {loading ? (
            <div className="vault-empty">Loading vault entries...</div>
          ) : hasVisibleGroups ? (
            <div className="vault-groups">{groupedCategoryNodes.map(renderCategoryGroup)}</div>
          ) : (
            <div className="vault-empty">{renderVaultEmptyState()}</div>
          )}
        </div>
      </section>

      {showEntryForm && (
        <div className="vault-modal-backdrop" onClick={closeEntryForm} role="presentation">
          <section
            className="vault-sheet-modal"
            onClick={(event) => event.stopPropagation()}
            role="dialog"
            aria-modal="true"
            aria-label={isEditing ? "Edit password" : "Add password"}
          >
            <div className="vault-sheet-header">
              <div className="vault-sheet-title">
                <button
                  type="button"
                  className="vault-sheet-back"
                  onClick={closeEntryForm}
                >
                  ALL ITEMS
                </button>
                <h2>{isEditing ? "Edit password" : "Add password"}</h2>
              </div>
              <button
                type="button"
                className="vault-icon-btn plain"
                onClick={closeEntryForm}
                aria-label="Close form"
              >
                <FaTimes />
              </button>
            </div>

            <form onSubmit={handleSubmit} className="vault-sheet-form">
              <label className="vault-field full">
                URL
                <input
                  type="url"
                  value={form.websiteUrl}
                  onChange={(e) => handleChange("websiteUrl", e.target.value)}
                  placeholder="https://example.com/login"
                  required
                />
              </label>

              <div className="vault-field-row">
                <label className="vault-field">
                  Name
                  <input
                    type="text"
                    value={form.siteName}
                    onChange={(e) => handleChange("siteName", e.target.value)}
                    placeholder="Example: Gmail"
                    required
                  />
                </label>

                <label className="vault-field">
                  Folder
                  <select
                    value={form.categoryPath}
                    onChange={(e) => handleChange("categoryPath", e.target.value)}
                    required
                  >
                    <option value="">Select folder</option>
                    {sortedCategories.map((category) => (
                      <option key={category.id} value={toFolderDisplayPath(category.name)}>
                        {toFolderDisplayPath(category.name)}
                      </option>
                    ))}
                  </select>
                </label>
              </div>

              <div className="vault-field-row">
                <label className="vault-field">
                  Username
                  <input
                    type="text"
                    value={form.username}
                    onChange={(e) => handleChange("username", e.target.value)}
                    placeholder="name@example.com"
                    required
                  />
                </label>

                <label className="vault-field">
                  Site password
                  <div className="vault-password-input">
                    <input
                      type={showFormPassword ? "text" : "password"}
                      value={form.password}
                      onChange={(e) => handleChange("password", e.target.value)}
                      placeholder={isEditing ? "Optional" : "Required"}
                      required={!isEditing}
                    />
                    <button
                      type="button"
                      className="vault-icon-btn plain"
                      onClick={() => setShowFormPassword((prev) => !prev)}
                      aria-label={showFormPassword ? "Hide password" : "Show password"}
                    >
                      {showFormPassword ? <FaEyeSlash /> : <FaEye />}
                    </button>
                  </div>
                </label>
              </div>

              <label className="vault-field full">
                Notes
                <textarea
                  value={form.notes}
                  onChange={(e) => handleChange("notes", e.target.value)}
                  placeholder="Recovery hints, account notes, etc."
                  rows={5}
                />
              </label>

              <div className="vault-sheet-footer">
                <button
                  type="button"
                  className="vault-btn ghost"
                  onClick={closeEntryForm}
                  disabled={saving}
                >
                  Cancel
                </button>
                <button type="submit" className="vault-btn primary" disabled={saving}>
                  {isEditing ? <FaSave /> : <FaPlus />}
                  {isEditing ? "Save Changes" : "Save"}
                </button>
              </div>
            </form>
          </section>
        </div>
      )}

      {showFolderForm && (
        <div className="vault-modal-backdrop" onClick={closeFolderForm} role="presentation">
          <section
            className="vault-modal vault-folder-modal"
            onClick={(event) => event.stopPropagation()}
            role="dialog"
            aria-modal="true"
            aria-label="Add folder"
          >
            <div className="vault-modal-header">
              <h2>{editingFolderId ? "Edit Folder" : "Add Folder"}</h2>
              <button
                type="button"
                className="vault-icon-btn"
                onClick={closeFolderForm}
                aria-label="Close add folder modal"
              >
                <FaTimes />
              </button>
            </div>

            <form className="vault-folder-form" onSubmit={handleSaveFolder}>
              <label className="vault-filter">
                Folder Name
                <input
                  type="text"
                  value={folderForm.name}
                  onChange={(event) => handleFolderFormChange("name", event.target.value)}
                  placeholder="Example: Brand Assets"
                  maxLength={80}
                  required
                />
              </label>

              <label className="vault-folder-check">
                <input
                  type="checkbox"
                  checked={folderForm.addAsSubfolder}
                  onChange={(event) =>
                    handleFolderFormChange("addAsSubfolder", event.target.checked)
                  }
                />
                Add as a subfolder
              </label>

              {folderForm.addAsSubfolder && (
                <label className="vault-filter">
                  Parent Folder
                  <select
                    value={folderForm.parentPath}
                    onChange={(event) =>
                      handleFolderFormChange("parentPath", event.target.value)
                    }
                    required
                  >
                    <option value="">Select folder</option>
                    {sortedCategories.map((category) => (
                      <option key={category.id} value={category.name}>
                        {formatCategoryOptionLabel(category.name)}
                      </option>
                    ))}
                  </select>
                </label>
              )}

              <div className="vault-form-actions">
                <button
                  type="button"
                  className="vault-btn ghost"
                  onClick={closeFolderForm}
                  disabled={folderSaving}
                >
                  Cancel
                </button>
                <button type="submit" className="vault-btn primary" disabled={folderSaving}>
                  {editingFolderId ? <FaSave /> : <FaFolderPlus />}
                  {editingFolderId
                    ? folderSaving
                      ? "Saving..."
                      : "Save Changes"
                    : folderSaving
                      ? "Creating..."
                      : "Create Folder"}
                </button>
              </div>
            </form>
          </section>
        </div>
      )}

      {showPermissionsModal && isAdmin && (
        <div
          className="vault-modal-backdrop"
          onClick={() => setShowPermissionsModal(false)}
          role="presentation"
        >
          <section
            className="vault-modal"
            onClick={(event) => event.stopPropagation()}
            role="dialog"
            aria-modal="true"
            aria-label="Folder permissions"
          >
            <div className="vault-modal-header">
              <h2>Folder Permissions</h2>
              <button
                type="button"
                className="vault-icon-btn"
                onClick={() => setShowPermissionsModal(false)}
                aria-label="Close permissions modal"
              >
                <FaTimes />
              </button>
            </div>

            <label className="vault-filter">
              Folder
              <select
                value={permissionCategoryId}
                onChange={(e) => setPermissionCategoryId(e.target.value)}
              >
                <option value="">Select folder</option>
                {sortedCategories.map((category) => (
                  <option key={category.id} value={category.id}>
                    {formatCategoryOptionLabel(category.name)}
                  </option>
                ))}
              </select>
            </label>

            {permissionsLoading ? (
              <div className="vault-empty">Loading permissions...</div>
            ) : employees.length === 0 ? (
              <div className="vault-empty">No standard users found.</div>
            ) : (
              <div className="vault-permissions-list">
                {employees.map((employee) => {
                  const enabled = Boolean(permissionsByEmployee[employee.id]);
                  return (
                    <div className="vault-permission-row" key={employee.id}>
                      <div>
                        <strong>{employee.name}</strong>
                        <span>{employee.email}</span>
                      </div>
                      <button
                        type="button"
                        className={`vault-toggle ${enabled ? "enabled" : "disabled"}`}
                        onClick={() => handlePermissionToggle(employee.id, !enabled)}
                        disabled={
                          !permissionCategoryId || permissionSavingIds[employee.id]
                        }
                      >
                        {enabled ? <FaCheck /> : null}
                        {enabled ? "Enabled" : "Disabled"}
                      </button>
                    </div>
                  );
                })}
              </div>
            )}
          </section>
        </div>
      )}

      <ConfirmationModal
        isOpen={Boolean(deleteTarget)}
        onClose={() => setDeleteTarget(null)}
        onConfirm={handleDelete}
        title="Delete Vault Entry"
        message={
          deleteTarget
            ? `Delete credentials for ${deleteTarget.siteName}?`
            : "Delete this vault entry?"
        }
        confirmText="Delete"
        cancelText="Cancel"
        isDangerous
      />

      <ConfirmationModal
        isOpen={Boolean(deleteFolderTarget)}
        onClose={() => setDeleteFolderTarget(null)}
        onConfirm={handleDeleteFolder}
        title="Delete Folder"
        message={
          deleteFolderTarget
            ? `Are you sure you want to delete the folder "${deleteFolderTarget.label}"? This will NOT delete the passwords inside, they will be moved to Uncategorized.`
            : "Delete this folder?"
        }
        confirmText="Delete Folder"
        cancelText="Cancel"
        isDangerous
      />

    </div>
  );
}

export default PasswordVault;
